import { Link, usePage } from "@inertiajs/react";
import {
  Anchor,
  Box,
  Center,
  Flex,
  Grid,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { ad_inspiration_img } from "@/assets/img";
import { IconCalendarMonth, IconAlignBoxCenterMiddle, IconBook2, IconRuler3 } from "@tabler/icons-react";
import { queryStudioAds, queryTemplates } from "@/Services";
import dayjs from "@/Utilities/dayjs-local";
import { TableActions } from "./AdBuilder/TableActions";
import { SvgTemplateHalfpage, SvgTemplateMedium, SvgTemplateBillboard } from ".";
import classes from "./RecentAdsList.module.css";
import { useTranslation } from "react-i18next";

export const RecentAdsList = () => {
  const { t } = useTranslation();
  const queryRecent = queryStudioAds({
    start: 0,
    length: 5,
    order_column: "created_at",
    order_dir: "desc",
  });

  const totalCount =
    queryRecent?.data?.recordsTotal ??
    queryRecent?.data?.total ??
    queryRecent?.data?.count ??
    queryRecent?.data?.data?.length ??
    0;

  return (
    <>
      <Box mb="lg">
        <Group gap="sm" mb={{ base: 16, sm: 'xl' }} align="center" className={classes.header}>
          <Title className={classes.title}>
            {t('ads.heading')}
          </Title>

          <Anchor
            component={Link}
            href={route("studio.ad_builder.index")}
            underline="never"
            className={classes.showMyAdsPill}
          >
            {t('ads.showMyAds')}
          </Anchor>
        </Group>

        <Stack gap="md" className={classes.list}>
          {queryRecent?.isFetching ? (
            <Center h={400}>
              <Loader />
            </Center>
          ) : (
            queryRecent?.data?.data?.map((item, index) => (
              <RecentAdCard key={`data-col1-${index}`} {...item} />
            ))
          )}
        </Stack>
      </Box>
    </>
  );
};

export const RecentAdCard = ({ id, name = "", ...props }) => {
  const { t } = useTranslation();
  const { data: templatesData } = queryTemplates();
  const { config } = usePage().props;

  const bannerFormatId = props?.banner_formats?.[0]?.id;
  const formatName = props?.banner_formats?.[0]?.name?.split(" (")[0] || "";

  const templateArr =
    templatesData?.data?.find(
      ({ alias }) => alias == props?.banner_formats?.[0]?.pivot?.tech_type
    ) || {};

  const imageSrc = `${config?.adProcessingUrl || ""}${props?.hash || ""}`;

  const formatIcon =
    {
      1: <IconAlignBoxCenterMiddle stroke={2} size={16} />,
      2: <IconBook2 stroke={2} size={16} />,
      3: <IconRuler3 stroke={2} size={16} />,
    }[bannerFormatId] || null;

  return (
    <>
      <Grid gutter="sm">
        <Grid.Col span={2} className={classes.thumbnailColumn}>
          <Paper bg="#F1F1F1" radius="sm" p={0} className={classes.thumbnail}>
            {bannerFormatId === 1 ? (
              <SvgTemplateMedium id={id} src={imageSrc} key={`image${id}`} height={120} />
            ) : bannerFormatId === 2 ? (
              <SvgTemplateHalfpage id={id} src={imageSrc} key={`image${id}`} height={120} />
            ) : bannerFormatId === 3 ? (
              <SvgTemplateBillboard id={id} src={imageSrc} key={`image${id}`} height={120} />
            ) : bannerFormatId === 4 ? (
              <SvgTemplateBillboard id={id} src={imageSrc} key={`image${id}`} height={120} />
            ) :(
              <Image
                radius="md"
                src={templateArr?.image}
                fallbackSrc={ad_inspiration_img}
                mah={85}
              />
            )}
          </Paper>
        </Grid.Col>

        <Grid.Col span={10} className={classes.metaColumn}>
          <Flex
            justify="space-between"
            align="center"
            wrap="nowrap"
            className={classes.metaRow}
          >
            <Stack style={{ minWidth: 0, flex: 1 }} gap={2} className={classes.nameStack}>
              <Title
                className={classes.adName}
                lineClamp={1}
                title={name}
              >
                <Text span inherit visibleFrom="sm">{id} | </Text>{name || t('common.notAvailable')}
              </Title>

              <Text hiddenFrom="sm" className={classes.subTitle}>{dayjs(props?.created_at).fromNow()}</Text>

              <Group gap={6} align="center" visibleFrom="sm" className={classes.formatRow}>
                {formatIcon}
                <Text fw="normal" className={classes.subTitle}>
                  {formatName}
                </Text>
              </Group>
            </Stack>

            <Group
              gap="md"
              align="center"
              wrap="nowrap"
              className={classes.actionsRow}
              style={{ flexShrink: 0 }}
            >
              <Group gap={4} align="center" mr={30} visibleFrom="sm" className={classes.timeRow}>
                <ThemeIcon variant="transparent" className={classes.subTitle}>
                  <IconCalendarMonth stroke={1} />
                </ThemeIcon>
                <Text fw="normal" className={classes.subTitle}>
                  {dayjs(props?.created_at).fromNow()}
                </Text>
              </Group>

              <TableActions row={{ original: { id, name, ad_origin: props?.ad_origin } }} />
            </Group>
          </Flex>
        </Grid.Col>
      </Grid>
    </>
  );
};
